/**
 * SNS Preview Live Render (V2)
 *
 * Render layer: DOM manipulation only.
 */

import { Roles } from "../../constants/roles";
import { SNSPlatform } from "../core/sns-platform";
import { SupportedPreviewPlatform } from "../core/supported-preview-platform";
import { SNSPreviewLiveUtils } from "./sns-preview-live-utils";
import { normalizeInstagramPermalinkForEmbed } from "../extractor/instagram-embed-extractor";
import { InstagramEmbedRender } from "../preview-manager/render/embed/instagram-embed-render";
import { buildFacebookPluginPostIframeSrc } from "../preview-manager/render/embed/facebook-embed-render.js";
import {
    buildXEmbedNode,
    ensureXEmbedWidgetsLoadedAndRefresh,
} from "../preview-manager/render/embed/x-embed-render.js";
import { CommonUtils } from "../../util/common-utils";
import { OgpRender } from "../../util/ogp/ogp-render";
import { snsPreviewLiveForm } from "./sns-preview-live-form";

const ogpRender = new OgpRender();

type RenderSnsPreviewOptions = {
    includeRemoveControls?: boolean;
    autoplay?: boolean;
};

export const SNS_IMAGE_FIRST_FLAGS: Readonly<Record<SupportedPreviewPlatform, boolean>> = {
    instagram: true,
    tiktok: false,
    youtube: true,
    facebook: true,
    x: true,
};

export class SNSPreviewLiveRender {
    public static getSupportedPreviewPlatform(rawUrl: string): SupportedPreviewPlatform | null {
        const meta = SNSPreviewLiveUtils.getUrlMeta(rawUrl);
        const platform = meta.platform || (/tiktok\.com\//i.test(rawUrl) ? SNSPlatform.TIKTOK : null);

        if (platform === SNSPlatform.INSTAGRAM) return "instagram";
        if (platform === SNSPlatform.TIKTOK) return "tiktok";
        if (platform === SNSPlatform.YOUTUBE) return "youtube";
        if (platform === SNSPlatform.FACEBOOK) return "facebook";
        if (platform === SNSPlatform.X) return "x";

        return null;
    }

    public static isImageFirstEnabled(platform: SupportedPreviewPlatform): boolean {
        return SNS_IMAGE_FIRST_FLAGS[platform] !== false;
    }

    private static createImagePreviewTile(
        permalink: string,
        platformClass: string,
        platform: SupportedPreviewPlatform,
    ): HTMLElement {
        return SNSPreviewLiveRender.createElementFromHtml(`
            <div
                class="picker-tile sns-preview-tile is-loading ${CommonUtils.escapeHtmlAttribute(platformClass)}"
                data-role="${CommonUtils.escapeHtmlAttribute(Roles.SNS_PREVIEW_TILE)}"
                data-sns-url="${CommonUtils.escapeHtmlAttribute(permalink)}"
                data-sns-platform="${CommonUtils.escapeHtmlAttribute(platform)}"
                data-preview-state="loading"
            >
                <div class="sns-preview-tile__loading" aria-hidden="true">
                    <div class="spinner spinner--inline-load" role="status" aria-hidden="true"></div>
                </div>
                <img
                    class="sns-preview-thumb"
                    data-role="${CommonUtils.escapeHtmlAttribute(Roles.SNS_PREVIEW_THUMB)}"
                    alt=""
                    loading="lazy"
                    hidden
                    aria-hidden="true"
                />
            </div>
        `);
    }

    private static async hydrateImagePreviewTile(tile: HTMLElement, permalink: string, alt: string): Promise<boolean> {
        const previewImageUrl = await ogpRender.fetchPreviewImageUrl(permalink);
        if (!previewImageUrl || !tile.isConnected) {
            if (tile.isConnected) {
                tile.classList.remove("is-loading");
                tile.dataset.previewState = "unavailable";
            }
            return false;
        }

        const image = snsPreviewLiveForm.getPreviewThumb(tile);
        if (!(image instanceof HTMLImageElement)) return false;

        image.src = previewImageUrl;
        image.alt = alt;
        image.hidden = false;
        image.setAttribute("aria-hidden", "false");
        tile.classList.remove("is-loading");
        tile.dataset.previewState = "image";
        return true;
    }

    private static createInstagramImageTile(permalink: string): HTMLElement {
        return SNSPreviewLiveRender.createImagePreviewTile(permalink, "sns-preview-tile--instagram", "instagram");
    }

    private static createTikTokImageTile(permalink: string): HTMLElement {
        return SNSPreviewLiveRender.createImagePreviewTile(permalink, "sns-preview-tile--tiktok", "tiktok");
    }

    private static createYouTubeImageTile(permalink: string): HTMLElement {
        return SNSPreviewLiveRender.createImagePreviewTile(permalink, "sns-preview-tile--youtube", "youtube");
    }

    private static createFacebookImageTile(permalink: string): HTMLElement {
        return SNSPreviewLiveRender.createImagePreviewTile(permalink, "sns-preview-tile--facebook", "facebook");
    }

    private static createXImageTile(permalink: string): HTMLElement {
        return SNSPreviewLiveRender.createImagePreviewTile(permalink, "sns-preview-tile--x", "x");
    }

    private static createEmbedTileShell(rawUrl: string, platform: SupportedPreviewPlatform): HTMLElement {
        return SNSPreviewLiveRender.createElementFromHtml(`
            <div
                class="picker-tile sns-preview-tile sns-preview-tile--embed sns-preview-tile--${CommonUtils.escapeHtmlAttribute(platform)}"
                data-role="${CommonUtils.escapeHtmlAttribute(Roles.SNS_PREVIEW_TILE)}"
                data-sns-url="${CommonUtils.escapeHtmlAttribute(rawUrl)}"
                data-sns-platform="${CommonUtils.escapeHtmlAttribute(platform)}"
                data-preview-state="embed"
            ></div>
        `);
    }

    private static finalizeEmbedTile(
        tile: HTMLElement,
        rawUrl: string,
        options?: RenderSnsPreviewOptions,
    ): HTMLElement {
        if (options?.includeRemoveControls !== false) {
            tile.appendChild(SNSPreviewLiveRender.createRemoveControls(rawUrl));
        }
        return tile;
    }

    private static createYouTubeEmbedTile(
        rawUrl: string,
        videoId: string,
        options?: RenderSnsPreviewOptions,
    ): HTMLElement {
        const autoplayQuery = options?.autoplay ? "autoplay=1&playsinline=1&rel=0" : "rel=0";
        const embedSrc = `https://www.youtube.com/embed/${encodeURIComponent(String(videoId))}?${autoplayQuery}`;
        const tile = this.createEmbedTileShell(rawUrl, "youtube");
        const embed = SNSPreviewLiveRender.createElementFromHtml(`
            <div class="sns-preview-embed sns-preview-embed--youtube" aria-hidden="true">
                <iframe
                    class="sns-preview-iframe"
                    src="${CommonUtils.escapeHtmlAttribute(embedSrc)}"
                    title="YouTube"
                    loading="lazy"
                    frameborder="0"
                    allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowfullscreen
                ></iframe>
            </div>
        `);
        tile.appendChild(embed);
        return this.finalizeEmbedTile(tile, rawUrl, options);
    }

    private static createFacebookEmbedTile(rawUrl: string, options?: RenderSnsPreviewOptions): HTMLElement {
        const src =
            buildFacebookPluginPostIframeSrc(rawUrl, { showText: false }) ||
            `https://www.facebook.com/plugins/post.php?href=${encodeURIComponent(rawUrl)}&show_text=false`;
        const tile = this.createEmbedTileShell(rawUrl, "facebook");
        const embed = SNSPreviewLiveRender.createElementFromHtml(`
            <div class="sns-preview-embed sns-preview-embed--facebook" aria-hidden="true">
                <iframe
                    class="sns-preview-iframe"
                    src="${CommonUtils.escapeHtmlAttribute(src)}"
                    title="Facebook"
                    width="500"
                    height="600"
                    loading="lazy"
                    scrolling="no"
                    frameborder="0"
                    allowfullscreen
                    sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                    referrerpolicy="strict-origin-when-cross-origin"
                    allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                ></iframe>
            </div>
        `);
        tile.appendChild(embed);
        return this.finalizeEmbedTile(tile, rawUrl, options);
    }

    private static createInstagramEmbedTile(rawUrl: string, options?: RenderSnsPreviewOptions): HTMLElement | null {
        const embedNode = InstagramEmbedRender.generateInstagramEmbedForTile(rawUrl);
        if (!(embedNode instanceof HTMLElement)) {
            return null;
        }

        const tile = this.createEmbedTileShell(rawUrl, "instagram");
        const embed = document.createElement("div");
        embed.className = "sns-preview-embed sns-preview-embed--instagram";
        embed.setAttribute("aria-hidden", "true");
        embed.appendChild(embedNode);
        tile.appendChild(embed);
        return this.finalizeEmbedTile(tile, rawUrl, options);
    }

    private static createXEmbedTile(rawUrl: string, options?: RenderSnsPreviewOptions): HTMLElement {
        const tile = this.createEmbedTileShell(rawUrl, "x");
        const embedHost = document.createElement("div");
        embedHost.className = "sns-preview-embed sns-preview-embed--x";
        embedHost.setAttribute("aria-hidden", "true");
        embedHost.appendChild(buildXEmbedNode(rawUrl));
        tile.appendChild(embedHost);
        return this.finalizeEmbedTile(tile, rawUrl, options);
    }

    private static createTikTokEmbedTile(rawUrl: string, id: string, options?: RenderSnsPreviewOptions): HTMLElement {
        const embedSrc = `https://www.tiktok.com/embed/v2/${encodeURIComponent(String(id))}${options?.autoplay ? "?autoplay=1" : ""}`;
        const tile = this.createEmbedTileShell(rawUrl, "tiktok");
        const embed = SNSPreviewLiveRender.createElementFromHtml(`
            <div class="sns-preview-embed sns-preview-embed--tiktok" aria-hidden="true">
                <iframe
                    class="sns-preview-iframe"
                    src="${CommonUtils.escapeHtmlAttribute(embedSrc)}"
                    title="TikTok"
                    loading="lazy"
                    frameborder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowfullscreen
                ></iframe>
            </div>
        `);
        tile.appendChild(embed);
        return this.finalizeEmbedTile(tile, rawUrl, options);
    }

    private static activateEmbedPostProcess(tile: HTMLElement, platform: SupportedPreviewPlatform): void {
        if (platform === "instagram") {
            InstagramEmbedRender.ensureInstagramEmbedJsLoadedAndRefresh();
            return;
        }

        if (platform === "x") {
            ensureXEmbedWidgetsLoadedAndRefresh(tile);
        }
    }

    private static createEmbedTile(
        rawUrl: string,
        platform: SupportedPreviewPlatform,
        options?: RenderSnsPreviewOptions,
    ): HTMLElement | null {
        const meta = SNSPreviewLiveUtils.getUrlMeta(rawUrl);

        if (platform === "youtube") {
            const videoId = meta.id;
            return videoId ? this.createYouTubeEmbedTile(rawUrl, videoId, options) : null;
        }

        if (platform === "facebook") {
            return this.createFacebookEmbedTile(rawUrl, options);
        }

        if (platform === "instagram") {
            return this.createInstagramEmbedTile(rawUrl, options);
        }

        if (platform === "x") {
            return this.createXEmbedTile(rawUrl, options);
        }

        const tikTokId = meta.id || String(rawUrl).match(/\/video\/(\d+)/i)?.[1] || "";
        return tikTokId ? this.createTikTokEmbedTile(rawUrl, tikTokId, options) : null;
    }

    private static renderImageFirstTile(
        container: HTMLElement,
        rawUrl: string,
        platform: SupportedPreviewPlatform,
        alt: string,
        createTile: (url: string) => HTMLElement,
        options?: RenderSnsPreviewOptions,
    ): void {
        const tile = createTile(rawUrl);
        if (options?.includeRemoveControls !== false) {
            tile.appendChild(SNSPreviewLiveRender.createRemoveControls(rawUrl));
        }
        container.appendChild(tile);

        void SNSPreviewLiveRender.hydrateImagePreviewTile(tile, rawUrl, alt).then((rendered) => {
            if (rendered || !tile.isConnected) {
                return;
            }

            const embedTile = SNSPreviewLiveRender.createEmbedTile(rawUrl, platform, options);
            if (!(embedTile instanceof HTMLElement)) {
                tile.classList.remove("is-loading");
                tile.dataset.previewState = "unavailable";
                return;
            }

            tile.replaceWith(embedTile);
            SNSPreviewLiveRender.activateEmbedPostProcess(embedTile, platform);
        });
    }

    public static activateSnsPreviewTile(tile: HTMLElement): void {
        const platform = String(tile.dataset.snsPlatform || "").trim() as SupportedPreviewPlatform;
        const rawUrl = String(tile.dataset.snsUrl || "").trim();
        const previewState = String(tile.dataset.previewState || "").trim();

        if (
            (platform !== "instagram" &&
                platform !== "tiktok" &&
                platform !== "youtube" &&
                platform !== "facebook" &&
                platform !== "x") ||
            !rawUrl ||
            previewState !== "image"
        ) {
            return;
        }

        const embedTile = this.createEmbedTile(rawUrl, platform, { autoplay: true });
        if (!(embedTile instanceof HTMLElement)) {
            return;
        }

        tile.replaceWith(embedTile);
        this.activateEmbedPostProcess(embedTile, platform);
    }

    /** Create an HTMLElement from an HTML string. */
    public static createElementFromHtml(html: string): HTMLElement {
        const template = document.createElement("template");
        template.innerHTML = String(html || "").trim();
        const element = template.content.firstElementChild;
        if (!(element instanceof HTMLElement)) {
            throw new Error("Failed to create element from HTML");
        }
        return element;
    }

    /** Update preview container with an array of SNS URLs. */
    public static updatePreviewWithUrls(urls: string[], container: HTMLElement): void {
        if (!container) return;

        const isInLinksPanel = Boolean(snsPreviewLiveForm.getClosestLinksPanel(container));

        container.replaceChildren();

        if (isInLinksPanel) {
            SNSPreviewLiveRender.renderAddTile(container);
        }

        if (!Array.isArray(urls) || urls.length === 0) {
            if (!isInLinksPanel) {
                SNSPreviewLiveRender.renderPlaceholderTile(container);
            }
            return;
        }

        urls.forEach((url) => {
            SNSPreviewLiveRender.renderSnsPreview(url, container);
        });
    }

    /** Render the "Add SNS Link" tile. */
    public static renderAddTile(container: HTMLElement): void {
        const button = SNSPreviewLiveRender.createElementFromHtml(`
            <button
                type="button"
                class="picker-tile picker-add sns-preview-add"
                data-role="${CommonUtils.escapeHtmlAttribute(Roles.SNS_ADD_TILE)}"
                aria-label="Add SNS Link"
            >
                <span class="picker-plus" aria-hidden="true">+</span>
                <span class="picker-add-text" data-translate="add_sns_link">Add SNS Link</span>
            </button>
        `);
        container.appendChild(button);
    }

    /** Render the empty placeholder tile. */
    public static renderPlaceholderTile(container: HTMLElement): void {
        const tile = SNSPreviewLiveRender.createElementFromHtml(`
            <button
                type="button"
                class="picker-tile sns-preview-placeholder"
                data-role="${CommonUtils.escapeHtmlAttribute(Roles.SNS_ADD_TILE)}"
                aria-label="เพิ่ม SNS ลิงก์"
            >
                <span class="picker-plus" aria-hidden="true">+</span>
                <span class="picker-add-text" data-translate="add_sns_link">เพิ่ม SNS ลิงก์</span>
            </button>
        `);
        container.appendChild(tile);
    }

    /** Render one SNS preview tile. */
    public static renderSnsPreview(url: string, container: HTMLElement, options?: RenderSnsPreviewOptions): void {
        const rawUrl = String(url || "");
        const safeUrlAttr = CommonUtils.escapeHtmlAttribute(rawUrl);
        const safeUrlText = CommonUtils.escapeHtml(rawUrl);

        const platform = SNSPreviewLiveRender.getSupportedPreviewPlatform(rawUrl);
        const forceImageFirst = Boolean(snsPreviewLiveForm.getClosestLinksPanel(container));

        if (platform && !forceImageFirst && !SNSPreviewLiveRender.isImageFirstEnabled(platform)) {
            const embedSourceUrl =
                platform === "instagram" ? normalizeInstagramPermalinkForEmbed(url) || rawUrl : rawUrl;
            const embedTile = SNSPreviewLiveRender.createEmbedTile(embedSourceUrl, platform, options);
            if (embedTile instanceof HTMLElement) {
                container.appendChild(embedTile);
                SNSPreviewLiveRender.activateEmbedPostProcess(embedTile, platform);
                return;
            }
        }

        if (platform === "youtube") {
            SNSPreviewLiveRender.renderImageFirstTile(
                container,
                rawUrl,
                "youtube",
                "YouTube preview",
                (permalink) => SNSPreviewLiveRender.createYouTubeImageTile(permalink),
                options,
            );
            return;
        }

        if (platform === "facebook") {
            SNSPreviewLiveRender.renderImageFirstTile(
                container,
                rawUrl,
                "facebook",
                "Facebook preview",
                (permalink) => SNSPreviewLiveRender.createFacebookImageTile(permalink),
                options,
            );
            return;
        }

        if (platform === "instagram") {
            const permalink = normalizeInstagramPermalinkForEmbed(url) || String(url || "");
            SNSPreviewLiveRender.renderImageFirstTile(
                container,
                permalink,
                "instagram",
                "Instagram preview",
                (value) => SNSPreviewLiveRender.createInstagramImageTile(value),
                options,
            );
            return;
        }

        if (platform === "x") {
            SNSPreviewLiveRender.renderImageFirstTile(
                container,
                rawUrl,
                "x",
                "X preview",
                (permalink) => SNSPreviewLiveRender.createXImageTile(permalink),
                options,
            );
            return;
        }

        if (platform === "tiktok") {
            SNSPreviewLiveRender.renderImageFirstTile(
                container,
                rawUrl,
                "tiktok",
                "TikTok preview",
                (permalink) => SNSPreviewLiveRender.createTikTokImageTile(permalink),
                options,
            );
            return;
        }

        const tile = SNSPreviewLiveRender.createElementFromHtml(`
            <div class="picker-tile sns-preview-tile" data-role="${CommonUtils.escapeHtmlAttribute(Roles.SNS_PREVIEW_TILE)}">
                <div class="sns-preview-tile-inner">
                    <i class="${CommonUtils.escapeHtmlAttribute(SNSPreviewLiveUtils.getPlatformIcon(url))}" aria-hidden="true"></i>
                    <a data-role="${CommonUtils.escapeHtmlAttribute(Roles.SNS_OPEN)}" href="${safeUrlAttr}" target="_blank" rel="noopener noreferrer" class="sns-preview-link">${safeUrlText}</a>
                </div>
            </div>
        `);
        if (options?.includeRemoveControls !== false) {
            tile.appendChild(SNSPreviewLiveRender.createRemoveControls(url));
        }
        container.appendChild(tile);
    }

    /** Create picker remove controls for one SNS tile. */
    public static createRemoveControls(url: string): HTMLElement {
        return SNSPreviewLiveRender.createElementFromHtml(`
            <div class="picker-controls" data-role="${CommonUtils.escapeHtmlAttribute(Roles.MEDIA_CONTROLS)}">
                <button
                    type="button"
                    class="btn circle-clear media-picker picker-remove"
                    data-role="${CommonUtils.escapeHtmlAttribute(Roles.SNS_TILE_REMOVE)}"
                    data-url="${CommonUtils.escapeHtmlAttribute(String(url || ""))}"
                    aria-label="Remove SNS Link"
                ></button>
            </div>
        `);
    }
}
